import { Button, Card, Inline, Stack, TextInput } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { MEMBER_ROLE_TO_BUILTIN_ID, type MemberRoleTag } from "../../../access/roles.js";
import { ApiError, apiSend } from "../../../lib/api.js";

type TenantType = "platform_only" | "managed_hardware" | "hybrid";

/**
 * Quelle: services/api/src/modules/billing-modules/catalog.ts
 * (`SELLABLE_MODULES`, Owner-Entscheidungen 2026-10-05/09) — die sellable
 * Modul-Schluessel des Produkts; Namen auch in
 * packages/i18n/src/catalogs/customer/en.json:4955-4961
 * (`modules.<key>.name`). "basis" ist Pflichtbestandteil jedes Plans (keine
 * Wahl); `video` ist noch kein Modul.
 */
const MODULE_CATALOG = [
  "maintenance",
  "fuel_costs",
  "reports",
  "genius_intelligence",
  "clean_city",
  "dispatch",
] as const;
type ModuleKey = (typeof MODULE_CATALOG)[number];
type ModuleKind = "grant" | "trial";

interface ModuleSelection {
  readonly kind: ModuleKind;
  readonly until: string;
}

const INITIAL_ADMIN_ROLES = Object.keys(MEMBER_ROLE_TO_BUILTIN_ID) as MemberRoleTag[];
const DEFAULT_ADMIN_ROLE: MemberRoleTag = "admin";

interface CreatedModule {
  readonly module: string;
  readonly kind: ModuleKind;
  readonly state: string;
  readonly until: string | null;
}

interface CreateTenantResponse {
  readonly id: string;
  readonly invitation?: { readonly id: string; readonly email: string } | null;
  readonly deliveryPreview?: {
    readonly token: string;
    readonly acceptUrl: string | null;
  } | null;
  readonly invite?: { readonly error: string } | null;
  readonly modules?: ReadonlyArray<CreatedModule> | null;
}

interface CreateSuccessSnapshot {
  readonly tenantId: string;
  readonly invitedEmail: string | null;
  readonly acceptUrl: string | null;
  readonly inviteError: string | null;
  readonly modules: ReadonlyArray<CreatedModule> | null;
}

/** 4xx module error per the fixed contract: `{ module, reason }` alongside the generic error. */
function parseModuleError(
  body: unknown,
): { readonly module: string; readonly reason: string } | null {
  if (body === null || typeof body !== "object" || !("module" in body)) return null;
  const candidate = body as {
    module: unknown;
    reason?: unknown;
    error?: unknown;
    message?: unknown;
  };
  if (typeof candidate.module !== "string") return null;
  const reason =
    typeof candidate.reason === "string"
      ? candidate.reason
      : typeof candidate.error === "string"
        ? candidate.error
        : typeof candidate.message === "string"
          ? candidate.message
          : "";
  return { module: candidate.module, reason };
}

/**
 * `<input type="date">` liefert YYYY-MM-DD ohne Zeit/Offset; die API
 * (services/api/src/modules/admin/tenants.dto.ts) verlangt
 * `z.string().datetime({ offset: true })`. Tagesende (nicht UTC-Mitternacht)
 * gewaehlt, weil startTrialFor (module-grants.service.ts) `endsAt` strikt
 * nach "jetzt" verlangt - UTC-Mitternacht des gewaehlten Tages koennte in
 * der Vergangenheit liegen, wenn lokale Zeitzone voraus ist.
 */
function moduleUntilToIso(dateOnly: string): string {
  return new Date(`${dateOnly}T23:59:59.999`).toISOString();
}

/**
 * Generic validation_error per services/api/src/plugins/error.ts:
 * `{ error: "validation_error", issues: [{ path, message }] }`. Maps an
 * `initialModules.<index>.until` issue back to the selected module so the
 * mask can show it instead of the generic "HTTP 400".
 */
function parseModuleUntilValidationError(
  body: unknown,
  selectedModules: readonly ModuleKey[],
): { readonly module: ModuleKey; readonly reason: string } | null {
  if (body === null || typeof body !== "object" || !("issues" in body)) return null;
  const candidate = body as { error?: unknown; issues?: unknown };
  if (candidate.error !== "validation_error" || !Array.isArray(candidate.issues)) return null;
  for (const issue of candidate.issues) {
    if (issue === null || typeof issue !== "object") continue;
    const { path, message } = issue as { path?: unknown; message?: unknown };
    if (typeof path !== "string") continue;
    const match = /^initialModules\.(\d+)\.until$/.exec(path);
    if (!match) continue;
    const module = selectedModules[Number(match[1])];
    if (!module) continue;
    return { module, reason: typeof message === "string" ? message : "" };
  }
  return null;
}

interface GtccCreateTenantFormProps {
  readonly onOpenTenant: (tenantId: string) => void;
}

/**
 * I-GTCC-18 — the single write action in the GTCC tenants view.
 *
 * This is a direct port of gtcc1's `CreateTenantForm`: the same four fields,
 * the same POST endpoint and the same success/downstream invitation states.
 * The production composition is gated by `GtccShell` before any GTCC view
 * mounts. The API independently repeats the guard with `requirePlatformOwner`.
 */
export function GtccCreateTenantForm({ onOpenTenant }: GtccCreateTenantFormProps) {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [packagePreset, setPackagePreset] = useState("Trial-100");
  const [type, setType] = useState<TenantType>("platform_only");
  const [initialAdminEmail, setInitialAdminEmail] = useState("");
  const [initialAdminRole, setInitialAdminRole] = useState<MemberRoleTag>(DEFAULT_ADMIN_ROLE);
  const [moduleSelections, setModuleSelections] = useState<
    Partial<Record<ModuleKey, ModuleSelection>>
  >({});
  const [moduleErrors, setModuleErrors] = useState<Partial<Record<ModuleKey, string>>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<CreateSuccessSnapshot | null>(null);

  const toggleModule = (module: ModuleKey, enabled: boolean) => {
    setModuleSelections((current) => {
      const next = { ...current };
      if (enabled) {
        next[module] = { kind: "grant", until: "" };
      } else {
        delete next[module];
      }
      return next;
    });
  };

  const setModuleKind = (module: ModuleKey, kind: ModuleKind) => {
    setModuleSelections((current) => {
      const existing = current[module];
      if (!existing) return current;
      return { ...current, [module]: { ...existing, kind } };
    });
  };

  const setModuleUntil = (module: ModuleKey, until: string) => {
    setModuleSelections((current) => {
      const existing = current[module];
      if (!existing) return current;
      return { ...current, [module]: { ...existing, until } };
    });
  };

  const createMutation = useMutation<CreateTenantResponse, Error, void>({
    mutationFn: () => {
      const initialModules = MODULE_CATALOG.filter((module) => moduleSelections[module]).map(
        (module) => {
          const selection = moduleSelections[module]!;
          return {
            module,
            kind: selection.kind,
            ...(selection.until.trim() ? { until: moduleUntilToIso(selection.until.trim()) } : {}),
          };
        },
      );
      return apiSend<CreateTenantResponse>("POST", "/v1/admin/tenants", {
        displayName: displayName.trim(),
        type,
        packagePreset,
        ...(initialAdminEmail.trim() ? { initialAdminEmail: initialAdminEmail.trim() } : {}),
        ...(initialModules.length > 0 ? { initialModules } : {}),
        ...(initialAdminRole !== DEFAULT_ADMIN_ROLE ? { initialAdminRole } : {}),
      });
    },
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ["gtcc", "ten", "tenants"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      setCreateSuccess({
        tenantId: response.id,
        invitedEmail: response.invitation?.email ?? null,
        acceptUrl: response.deliveryPreview?.acceptUrl ?? null,
        inviteError: response.invite?.error ?? null,
        modules: response.modules ?? null,
      });
      setDisplayName("");
      setInitialAdminEmail("");
      setErrorMessage(null);
      setModuleErrors({});
      setModuleSelections({});
      setInitialAdminRole(DEFAULT_ADMIN_ROLE);
    },
    onError: (error) => {
      const moduleError = error instanceof ApiError ? parseModuleError(error.body) : null;
      if (moduleError) {
        setModuleErrors({
          [moduleError.module as ModuleKey]: t("admin.gtcc.ten.create.error.module_scoped", {
            module: moduleError.module,
            reason: moduleError.reason,
          }),
        });
        return;
      }
      const untilError =
        error instanceof ApiError
          ? parseModuleUntilValidationError(
              error.body,
              MODULE_CATALOG.filter((module) => moduleSelections[module]),
            )
          : null;
      if (untilError) {
        setModuleErrors({
          [untilError.module]: t("admin.gtcc.ten.create.error.module_until_invalid", {
            reason: untilError.reason,
          }),
        });
        return;
      }
      setModuleErrors({});
      setErrorMessage(error instanceof ApiError ? `HTTP ${error.status}` : error.message);
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setCreateSuccess(null);
    setModuleErrors({});
    if (!displayName.trim()) {
      setErrorMessage(t("admin.gtcc.ten.create.error.name_required"));
      return;
    }
    createMutation.mutate();
  };

  return (
    <Card elevation={1} data-testid="gtcc-ten-create">
      <form onSubmit={onSubmit}>
        <Stack gap={3}>
          <strong>{t("admin.gtcc.ten.create.title")}</strong>
          <Inline gap={2} wrap>
            <TextInput
              label={t("admin.gtcc.ten.create.field.display_name")}
              value={displayName}
              onChange={(event) => setDisplayName(event.currentTarget.value)}
              data-testid="gtcc-ten-create-name"
            />
            <label style={fieldStyle}>
              <span style={fieldLabelStyle}>{t("admin.gtcc.ten.create.field.type")}</span>
              <select
                value={type}
                onChange={(event) => setType(event.currentTarget.value as TenantType)}
                data-testid="gtcc-ten-create-type"
                style={selectStyle}
              >
                <option value="platform_only">{t("admin.gtcc.ten.type.platform_only")}</option>
                <option value="managed_hardware">
                  {t("admin.gtcc.ten.type.managed_hardware")}
                </option>
                <option value="hybrid">{t("admin.gtcc.ten.type.hybrid")}</option>
              </select>
            </label>
            <label style={fieldStyle}>
              <span style={fieldLabelStyle}>{t("admin.gtcc.ten.create.field.package")}</span>
              <select
                value={packagePreset}
                onChange={(event) => setPackagePreset(event.currentTarget.value)}
                data-testid="gtcc-ten-create-package"
                style={selectStyle}
              >
                <option value="Trial-100">Trial-100</option>
                <option value="Starter-100">Starter-100</option>
                <option value="Pro-500">Pro-500</option>
                <option value="Pro-1000">Pro-1000</option>
                <option value="Enterprise-Unlimited">Enterprise-Unlimited</option>
                <option value="Custom">Custom</option>
              </select>
            </label>
          </Inline>

          <Card elevation={0}>
            <Stack gap={1} data-testid="gtcc-ten-create-invite-section">
              <strong style={fieldLabelStyle}>
                {t("admin.gtcc.ten.create.invite_section.title")}
              </strong>
              <span style={mutedTextStyle}>{t("admin.gtcc.ten.create.invite_section.body")}</span>
              <TextInput
                label={t("admin.gtcc.ten.create.field.initial_admin_email")}
                type="email"
                value={initialAdminEmail}
                onChange={(event) => setInitialAdminEmail(event.currentTarget.value)}
                data-testid="gtcc-ten-create-email"
              />
              <label style={fieldStyle}>
                <span style={fieldLabelStyle}>
                  {t("admin.gtcc.ten.create.field.initial_admin_role")}
                </span>
                <select
                  value={initialAdminRole}
                  onChange={(event) =>
                    setInitialAdminRole(event.currentTarget.value as MemberRoleTag)
                  }
                  data-testid="gtcc-ten-create-role"
                  style={selectStyle}
                >
                  {INITIAL_ADMIN_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {t(`admin.gtcc.ten.member_role.${role}`)}
                    </option>
                  ))}
                </select>
              </label>
            </Stack>
          </Card>

          <Card elevation={0}>
            <Stack gap={1} data-testid="gtcc-ten-create-modules-section">
              <strong style={fieldLabelStyle}>
                {t("admin.gtcc.ten.create.modules_section.title")}
              </strong>
              <span style={mutedTextStyle}>{t("admin.gtcc.ten.create.modules_section.body")}</span>
              {MODULE_CATALOG.map((module) => {
                const selection = moduleSelections[module];
                return (
                  <Stack gap={1} key={module} data-testid={`gtcc-ten-create-module-${module}`}>
                    <label style={inlineLabelStyle}>
                      <input
                        type="checkbox"
                        checked={Boolean(selection)}
                        onChange={(event) => toggleModule(module, event.currentTarget.checked)}
                        data-testid={`gtcc-ten-create-module-${module}-toggle`}
                      />
                      {t(`admin.gtcc.ten.create.module.${module}.name`)}
                    </label>
                    {selection ? (
                      <Inline gap={2} wrap>
                        <select
                          value={selection.kind}
                          onChange={(event) =>
                            setModuleKind(module, event.currentTarget.value as ModuleKind)
                          }
                          data-testid={`gtcc-ten-create-module-${module}-kind`}
                          style={selectStyle}
                        >
                          <option value="grant">
                            {t("admin.gtcc.ten.create.module.kind.grant")}
                          </option>
                          <option value="trial">
                            {t("admin.gtcc.ten.create.module.kind.trial")}
                          </option>
                        </select>
                        <label style={fieldStyle}>
                          <span style={fieldLabelStyle}>
                            {t("admin.gtcc.ten.create.module.until_label")}
                          </span>
                          <input
                            type="date"
                            value={selection.until}
                            onChange={(event) => setModuleUntil(module, event.currentTarget.value)}
                            data-testid={`gtcc-ten-create-module-${module}-until`}
                            style={selectStyle}
                          />
                        </label>
                      </Inline>
                    ) : null}
                    {moduleErrors[module] ? (
                      <span
                        role="alert"
                        style={errorTextStyle}
                        data-testid={`gtcc-ten-create-module-${module}-error`}
                      >
                        {moduleErrors[module]}
                      </span>
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
          </Card>

          {errorMessage ? (
            <span role="alert" style={errorTextStyle} data-testid="gtcc-ten-create-error">
              {errorMessage}
            </span>
          ) : null}

          {createSuccess ? (
            <GtccCreateTenantSuccess
              snapshot={createSuccess}
              onOpenTenant={onOpenTenant}
              onDismiss={() => setCreateSuccess(null)}
            />
          ) : null}

          <Inline gap={2}>
            <Button
              type="submit"
              variant="primary"
              disabled={createMutation.isPending}
              data-testid="gtcc-ten-create-submit"
            >
              {createMutation.isPending
                ? t("admin.gtcc.ten.create.saving")
                : t("admin.gtcc.ten.create.submit")}
            </Button>
            <span style={mutedTextStyle}>{t("admin.gtcc.ten.create.invite_note")}</span>
          </Inline>
        </Stack>
      </form>
    </Card>
  );
}

function GtccCreateTenantSuccess({
  snapshot,
  onOpenTenant,
  onDismiss,
}: {
  readonly snapshot: CreateSuccessSnapshot;
  readonly onOpenTenant: (tenantId: string) => void;
  readonly onDismiss: () => void;
}) {
  const { t } = useLocale();
  const hasInvite = Boolean(snapshot.invitedEmail);

  return (
    <Card elevation={1}>
      <Stack gap={2} data-testid="gtcc-ten-create-next-steps">
        <strong>
          {t(
            hasInvite
              ? "admin.gtcc.ten.create.next_steps.title_with_invite"
              : "admin.gtcc.ten.create.next_steps.title_no_invite",
          )}
        </strong>
        <span style={mutedBodyStyle}>
          {t(
            hasInvite
              ? "admin.gtcc.ten.create.next_steps.body_with_invite"
              : "admin.gtcc.ten.create.next_steps.body_no_invite",
          )}
        </span>
        {snapshot.invitedEmail && snapshot.acceptUrl ? (
          <Stack gap={1}>
            <span style={fieldLabelStyle}>
              {t("admin.gtcc.ten.create.next_steps.accept_url_label")} — {snapshot.invitedEmail}
            </span>
            <code data-testid="gtcc-ten-create-accept-url" style={acceptUrlStyle}>
              {snapshot.acceptUrl}
            </code>
            <Inline gap={2}>
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  if (typeof navigator !== "undefined" && navigator.clipboard) {
                    void navigator.clipboard.writeText(snapshot.acceptUrl!);
                  }
                }}
                data-testid="gtcc-ten-create-accept-url-copy"
              >
                {t("admin.gtcc.ten.create.next_steps.accept_url_copy")}
              </Button>
            </Inline>
          </Stack>
        ) : null}
        {snapshot.invitedEmail && !snapshot.acceptUrl && !snapshot.inviteError ? (
          <span data-testid="gtcc-ten-create-accept-url-missing" style={mutedTextStyle}>
            {t("admin.gtcc.ten.create.next_steps.accept_url_missing")}
          </span>
        ) : null}
        {snapshot.inviteError ? (
          <span role="alert" data-testid="gtcc-ten-create-invite-error" style={errorTextStyle}>
            {snapshot.inviteError}
          </span>
        ) : null}
        {snapshot.modules && snapshot.modules.length > 0 ? (
          <Stack gap={1} data-testid="gtcc-ten-create-next-steps-modules">
            <span style={fieldLabelStyle}>
              {t("admin.gtcc.ten.create.next_steps.modules_title")}
            </span>
            <ul style={moduleListStyle}>
              {snapshot.modules.map((createdModule) => (
                <li
                  key={createdModule.module}
                  data-testid={`gtcc-ten-create-next-steps-module-${createdModule.module}`}
                >
                  {t(`admin.gtcc.ten.create.module.${createdModule.module}.name`)} —{" "}
                  {t(`admin.gtcc.ten.create.module.kind.${createdModule.kind}`)}
                  {createdModule.until ? ` (${createdModule.until})` : ""}
                </li>
              ))}
            </ul>
          </Stack>
        ) : null}
        <Inline gap={2}>
          <Button
            variant="primary"
            type="button"
            onClick={() => onOpenTenant(snapshot.tenantId)}
            data-testid="gtcc-ten-create-open-tenant"
          >
            {t("admin.gtcc.ten.create.next_steps.tenant_360")}
          </Button>
          <Button variant="ghost" type="button" onClick={onDismiss}>
            {t("common.clear")}
          </Button>
        </Inline>
      </Stack>
    </Card>
  );
}

const fieldStyle = {
  display: "grid",
  gap: "var(--gt-sys-space-1)",
} as const;

const fieldLabelStyle = {
  fontSize: "var(--gt-sys-font-size-sm)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
} as const;

const mutedTextStyle = {
  color: "var(--gt-sys-text-muted)",
  fontSize: "var(--gt-sys-font-size-xs)",
} as const;

const mutedBodyStyle = {
  color: "var(--gt-sys-text-muted)",
  fontSize: "var(--gt-sys-font-size-sm)",
} as const;

const errorTextStyle = {
  color: "var(--gt-sys-danger)",
  fontSize: "var(--gt-sys-font-size-sm)",
} as const;

const selectStyle = {
  fontSize: "var(--gt-sys-font-size-sm)",
  paddingBlock: "var(--gt-sys-space-2)",
  paddingInline: "var(--gt-sys-space-3)",
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-sm)",
} as const;

const inlineLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-2)",
  fontSize: "var(--gt-sys-font-size-sm)",
} as const;

const moduleListStyle = {
  margin: 0,
  paddingInlineStart: "var(--gt-sys-space-5)",
  fontSize: "var(--gt-sys-font-size-sm)",
} as const;

const acceptUrlStyle = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  background: "var(--gt-sys-surface)",
  padding: "var(--gt-sys-space-2) var(--gt-sys-space-3)",
  borderRadius: "var(--gt-sys-radius-sm)",
  wordBreak: "break-all",
  fontSize: "var(--gt-sys-font-size-xs)",
} as const;
